import type { ChatFilterMode } from "@/features/Chat/types/chatMessageTypes";
import type { RequestGroup, ServiceRequest } from "@curai/js-fhir-tools";

export const QueryKeys = {
  User: {
    DEFAULT: ["user"],
    ME: ["user", "me"],
  },
  Admin: {
    DEFAULT: ["admin", "fhir"],
    RESOURCE: (resourceType?: string, id?: string) => [
      "admin",
      "fhir",
      resourceType,
      id,
    ],
    HISTORY: (resourceType?: string, id?: string) => [
      "admin",
      "fhir",
      resourceType,
      id,
      "_history",
    ],
    SEARCH: (resourceType?: string, params?: Record<string, unknown>) => [
      "admin",
      "fhir",
      "search",
      resourceType,
      params,
    ],
    EVERYTHING: (patientId?: string) => [
      "admin",
      "fhir",
      "patient-everything",
      patientId,
    ],
  },
  Provider: {
    DEFAULT: ["provider"],
    HEARTBEAT: ["provider", "heartbeat"],
    TASK_LIST: ["provider", "task-list"],
    TASK_LIST_COUNT: ["provider", "task-list-count"],
    TASK_LIST_AVAILABLE_ACTIONS: ["provider", "task-list-available-actions"],
    ONLINE_PRACTITIONERS: ["provider", "online-practitioners"],
    ShiftStatus: {
      DEFAULT: ["provider", "shift-status"],
      UPDATE: ["provider", "shift-status", "update"],
    },
    PATIENT_TABS: ["provider", "patient-tabs"],
    Search: {
      DEFAULT: ["provider", "search"],
      // @ts-ignore any params
      PATIENT: (params) => ["provider", "search", "patient", params],
    },
    LiveQueueDashboard: {
      DEFAULT: ["provider", "waiting-room-dashboard"],
      WITH_PARAMS: (params: { mineOnly?: boolean; abridged?: boolean }) => [
        "provider",
        "waiting-room-dashboard",
        params,
      ],
      COUNT: ["provider", "waiting-room-dashboard", "count"],
    },
    AsyncMessageDashboard: {
      DEFAULT: ["provider", "async-message-dashboard"],
      INBOX: ["provider", "async-message-dashboard", "inbox"],
      ARCHIVE: ["provider", "async-message-dashboard", "archive"],
      PINS: ["provider", "async-message-dashboard", "pinned"],
      PIN_PATIENT: (patientId: string) => [
        "provider",
        "async-message-dashboard",
        "pin-patient",
        patientId,
      ],
      STATUS_CHANGE: (patientId: string) => [
        "provider",
        "async-message-dashboard",
        "status-change",
        patientId,
      ],
      UNPIN_PATIENT: (patientId: string) => [
        "provider",
        "async-message-dashboard",
        "unpin-patient",
        patientId,
      ],
    },
    Pharmacy: {
      // @ts-ignore any params
      SEARCH: (params) => ["provider", "pharmacy", "search", params],
    },
  },
  /**
   * QUERY KEY CONVENTION — Patient-scoped keys
   *
   * Rule: `patientId` MUST appear in the key array immediately after the
   * first static namespace segment (i.e. as the second element for flat
   * Patient.* keys, or as the third element for namespaced keys like
   * Patient.MedicalRecords.*, Patient.Chart.*, Patient.Chat.*).
   *
   * ✅  ["patient", patientId, ...]
   * ✅  ["patient", "medical-records", patientId, ...]
   * ✅  ["patient", "chart",           patientId, ...]
   * ✅  ["patient", "chat",            patientId, ...]
   * ❌  ["patient", "something", "something-else", ..., patientId]
   *
   * Placing patientId at the end (or anywhere after other static strings)
   * breaks prefix-based cache invalidation — invalidateQueries uses the
   * patientId-anchored prefix to scope evictions to one patient at a time.
   */
  Patient: {
    DEFAULT: (patientId?: string) => ["patient", patientId],
    Conditions: {
      REMOVE_DIAGNOSIS: ({
        patientId,
        encounterId,
      }: {
        patientId: string;
        encounterId: string;
      }) => [
        "patient",
        patientId,
        "conditions",
        encounterId,
        "remove-diagnosis",
      ],
      ADD_DIAGNOSIS: ({
        patientId,
        encounterId,
      }: {
        patientId: string;
        encounterId: string;
      }) => ["patient", patientId, "conditions", encounterId, "add-diagnosis"],
      REORDER_DIAGNOSIS: ({
        patientId,
        encounterId,
      }: {
        patientId: string;
        encounterId: string;
      }) => [
        "patient",
        patientId,
        "conditions",
        encounterId,
        "reorder-diagnosis",
      ],
    },
    PreferredPharmacy: {
      DEFAULT: (patientId: string) => [
        "patient",
        "preferred-pharmacy",
        patientId,
      ],
      SET_PHARMACY: (patientId: string) => [
        "patient",
        "preferred-pharmacy",
        patientId,
        "set-pharmacy",
      ],
    },
    Demographics: {
      DEFAULT: (patientId?: string) => ["patient", "demographics", patientId],
    },
    Duplicates: {
      DEFAULT: (patientId: string) => ["patient", "duplicates", patientId],
    },
    MedicalRecords: {
      PatientId: (patientId?: string) => [
        "patient",
        "medical-records",
        patientId,
      ],
      AiFeed: {
        DEFAULT: (patientId: string, encounterId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "ai-feed",
          encounterId,
        ],
        ACCEPT: (patientId: string, encounterId: string, itemId: number) => [
          "patient",
          "medical-records",
          patientId,
          "ai-feed",
          encounterId,
          "accept",
          itemId,
        ],
        REJECT: (patientId: string, encounterId: string, itemId: number) => [
          "patient",
          "medical-records",
          patientId,
          "ai-feed",
          encounterId,
          "reject",
          itemId,
        ],
      },
      Alerts: {
        DEFAULT: (patientId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "flags",
        ],
      },
      WorkNotes: {
        DEFAULT: (patientId: string) => [
          "patient",
          "medical-records",
          "work-notes",
          patientId,
        ],
        CREATE: (patientId: string) => [
          "patient",
          "medical-records",
          "work-notes",
          "create",
          patientId,
        ],
        SEND: (patientId: string, workNoteId: number) => [
          "patient",
          "medical-records",
          "work-notes",
          patientId,
          "send",
          workNoteId,
        ],
        AVAILABLE_ACTIONS: (patientId: string) => [
          "patient",
          "medical-records",
          "work-notes",
          patientId,
          "available-actions",
        ],
      },
      Allergies: {
        AVAILABLE_CHARTING_ACTIONS: (patientId: string) => [
          "patient",
          "medical-records",
          patientId,
          "allergies",
          "available-actions",
        ],
        DEFAULT: (patientId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "allergies",
        ],
        BATCH_CONFIRM: (patientId: string) => [
          "patient",
          "medical-records",
          patientId,
          "allergies",
          "batch-confirm",
        ],
        BATCH_CREATE: (patientId: string) => [
          "patient",
          "medical-records",
          patientId,
          "allergies",
          "batch-create",
        ],
        STATUS_SECTION: (status: string, patientId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "allergies",
          status,
        ],
        CREATE: (patientId: string) => [
          "patient",
          "medical-records",
          patientId,
          "allergies",
          "create",
        ],
        UPDATE: (patientId: string, allergyIntoleranceId: string) => [
          "patient",
          "medical-records",
          patientId,
          "allergies",
          allergyIntoleranceId,
          "update",
        ],
        ACTIVATE: (patientId: string, allergyIntoleranceId: string) => [
          "patient",
          "medical-records",
          patientId,
          "allergies",
          allergyIntoleranceId,
          "activate",
        ],
        INACTIVATE: (patientId: string, allergyIntoleranceId: string) => [
          "patient",
          "medical-records",
          patientId,
          "allergies",
          allergyIntoleranceId,
          "inactivate",
        ],
        RESOLVE: (patientId: string, allergyIntoleranceId: string) => [
          "patient",
          "medical-records",
          patientId,
          "allergies",
          allergyIntoleranceId,
          "resolve",
        ],
        VOID: (patientId: string, allergyIntoleranceId: string) => [
          "patient",
          "medical-records",
          patientId,
          "allergies",
          allergyIntoleranceId,
          "void",
        ],
        CONFIRM: (patientId: string, allergyIntoleranceId: string) => [
          "patient",
          "medical-records",
          patientId,
          "allergies",
          allergyIntoleranceId,
          "confirm",
        ],
        MARK_NKDA: (patientId: string) => [
          "patient",
          "medical-records",
          patientId,
          "allergies",
          "mark-nkda",
        ],
        LAST_UPDATE_AGENT: (
          patientId: string,
          allergyIntoleranceId: string,
        ) => [
          "patient",
          "medical-records",
          patientId,
          "allergies",
          allergyIntoleranceId,
          "last-update-agent",
        ],
      },
      Conditions: {
        Search: {
          DEFAULT: ({
            patientId,
            searchTerm,
          }: { patientId: string; searchTerm?: string }) =>
            [
              "patient",
              "medical-records",
              patientId,
              "conditions",
              searchTerm,
            ].filter(Boolean),
        },
      },
      ExternalCareTeam: {
        DEFAULT: (patientId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "external-care-team",
        ],
        PROVIDER_SEARCH: ({
          searchTerm,
          state,
        }: {
          searchTerm: string;
          state?: string;
        }) => ["external-provider-search", searchTerm, state],
      },
      FamilyMemberHistory: {
        DEFAULT: (patientId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "family-member-history",
        ],
        OPTIONS: (patientId: string) => [
          "patient",
          "medical-records",
          patientId,
          "family-member-history",
          "options",
        ],
        CREATE: (patientId: string) => [
          "patient",
          "medical-records",
          patientId,
          "family-member-history",
          "create",
        ],
        VOID: (patientId: string, familyMemberHistoryId: string) => [
          "patient",
          "medical-records",
          patientId,
          "family-member-history",
          familyMemberHistoryId,
          "void",
        ],
        UPDATE: (patientId: string, familyMemberHistoryId: string) => [
          "patient",
          "medical-records",
          patientId,
          "family-member-history",
          familyMemberHistoryId,
          "update",
        ],
      },
      Files: {
        DEFAULT: (patientId?: string, categories?: string) => [
          "patient",
          "medical-records",
          patientId,
          "files",
          categories,
        ],
        BY_ID: (patientId?: string, documentReferenceId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "files",
          documentReferenceId,
        ],
        CREATE: (patientId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "files",
          "create",
        ],
        UPDATE: (patientId?: string, documentReferenceId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "files",
          "update",
          documentReferenceId,
        ],
      },
      GenericObservations: {
        DEFAULT: (patientId?: string, group?: string) => [
          "patient",
          "medical-records",
          patientId,
          "generic-observations",
          group,
        ],
        CREATE: (patientId?: string, group?: string) => [
          "patient",
          "medical-records",
          patientId,
          "generic-observations",
          group,
          "create",
        ],
        HISTORY: (patientId: string, group: string, code: string) => [
          "patient",
          "medical-records",
          patientId,
          "generic-observations",
          group,
          code,
          "history",
        ],
      },
      Goals: {
        DEFAULT: (patientId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "goals",
        ],
      },
      HealthInsurance: {
        DEFAULT: (patientId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "health-insurance",
        ],
        PAYORS: ["health-insurance", "payors"],
        CARRIERS: ["health-insurance", "carriers"],
      },
      Hospitalization: {
        DEFAULT: (patientId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "hospitalization",
        ],
        CREATE: (patientId: string) => [
          "patient",
          "medical-records",
          patientId,
          "hospitalization",
          "create",
        ],
        VOID: (patientId: string, hospitalizationId: string) => [
          "patient",
          "medical-records",
          patientId,
          "hospitalization",
          hospitalizationId,
          "void",
        ],
      },
      Immunization: {
        DEFAULT: (patientId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "immunization",
        ],
        CREATE: (patientId: string) => [
          "patient",
          "medical-records",
          patientId,
          "immunization",
          "create",
        ],
        VOID: (patientId: string, immunizationId: string) => [
          "patient",
          "medical-records",
          patientId,
          "immunization",
          immunizationId,
          "void",
        ],
      },
      Labs: {
        ORDER_HISTORY_AVAILABLE_ACTIONS: (patientId: string) => [
          "patient",
          "labs",
          patientId,
          "order-history-available-actions",
        ],
        DEFAULT: (patientId?: string) => ["patient", "labs", patientId],
        RELATED_RESOURCES: (patientId: string, serviceRequestId: string) => [
          "patient",
          "labs",
          patientId,
          "related-resources",
          serviceRequestId,
        ],
        ORDER_RESULT_HISTORY: (patientId: string, testCode: string) => [
          "patient",
          "labs",
          patientId,
          "order-result-history",
          testCode,
        ],
        SUBMIT_TRIAGE: (patientId: string, serviceRequestId: string) => [
          "patient",
          "labs",
          patientId,
          "submit-triage",
          serviceRequestId,
        ],
        FINALIZE_REVIEW: (patientId: string, serviceRequestId: string) => [
          "patient",
          "labs",
          patientId,
          "finalize-review",
          serviceRequestId,
        ],
        UPLOAD_ORDER_REQUISITION: (
          patientId: string,
          requestGroupId: string,
          serviceRequestId: string,
        ) => [
          "patient",
          "labs",
          patientId,
          "upload-order-requisition",
          requestGroupId,
          serviceRequestId,
        ],
        UPLOAD_RESULTS_REPORT: (
          patientId: string,
          serviceRequestId: string,
        ) => [
          "patient",
          "labs",
          patientId,
          "upload-results-report",
          serviceRequestId,
        ],
        SUBMIT_MANUAL_RESULTS: (
          patientId: string,
          serviceRequestId: string,
        ) => [
          "patient",
          "labs",
          patientId,
          "submit-manual-results",
          serviceRequestId,
        ],
        MARK_DISCUSSED: (patientId: string, serviceRequestId: string) => [
          "patient",
          "labs",
          patientId,
          "mark-discussed",
          serviceRequestId,
        ],
        DX_REPORT_VIEW: (patientId: string, diagnosticReportId: string) => [
          "patient",
          "labs",
          patientId,
          "dx-report-view",
          diagnosticReportId,
        ],
        UPDATE_RESULT_SUMMARY: (
          patientId: string,
          diagnosticReportId: string,
        ) => [
          "patient",
          "labs",
          patientId,
          "update-result-summary",
          diagnosticReportId,
        ],
        ORDER_AOE: (patientId: string, requestGroupId: string) => [
          "patient",
          "labs",
          patientId,
          "order-aoe",
          requestGroupId,
        ],
        VOID_FAILED_ORDER_BY_TEST: (
          patientId: string,
          serviceRequestId: string,
        ) => [
          "patient",
          "labs",
          patientId,
          "void-failed-order-by-test",
          serviceRequestId,
        ],
      },
      MedicalHistory: {
        DEFAULT: (patientId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "medical-history",
        ],
        PROBLEM_LIST: (patientId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "medical-history",
          "problem-list",
        ],
        CREATE: (patientId: string) => [
          "patient",
          "medical-records",
          patientId,
          "medical-history",
          "create",
        ],
        RESOLVE: (patientId: string, episodeOfCareId: string) => [
          "patient",
          "medical-records",
          patientId,
          "medical-history",
          episodeOfCareId,
          "resolve",
        ],
        VOID: (patientId: string, episodeOfCareId: string) => [
          "patient",
          "medical-records",
          patientId,
          "medical-history",
          episodeOfCareId,
          "void",
        ],
        REACTIVATE: (patientId: string, episodeOfCareId: string) => [
          "patient",
          "medical-records",
          patientId,
          "medical-history",
          episodeOfCareId,
          "reactivate",
        ],
        CONFIRM: (patientId: string, episodeOfCareId: string) => [
          "patient",
          "medical-records",
          patientId,
          "medical-history",
          episodeOfCareId,
          "confirm",
        ],
        CONFIRM_MULTIPLE: (patientId: string, episodeOfCareIds: string[]) => [
          "patient",
          "medical-records",
          patientId,
          "medical-history",
          "confirm-multiple",
          episodeOfCareIds,
        ],
        UPDATE: (patientId: string, episodeOfCareId: string) => [
          "patient",
          "medical-records",
          patientId,
          "medical-history",
          episodeOfCareId,
          "update",
        ],
        BATCH_CREATE: (patientId: string) => [
          "patient",
          "medical-records",
          patientId,
          "medical-history",
          "batch-create",
        ],
        CONDITION_RESOLVE: (patientId: string, conditionId: string) => [
          "patient",
          "medical-records",
          patientId,
          "medical-history",
          "condition",
          conditionId,
          "resolve",
        ],
        CONDITION_ACTIVATE: (patientId: string, conditionId: string) => [
          "patient",
          "medical-records",
          patientId,
          "medical-history",
          "condition",
          conditionId,
          "activate",
        ],
        CONDITION_CONFIRM: (patientId: string, conditionId: string) => [
          "patient",
          "medical-records",
          patientId,
          "medical-history",
          "condition",
          conditionId,
          "confirm",
        ],
        CONDITION_VOID: (patientId: string, conditionId: string) => [
          "patient",
          "medical-records",
          patientId,
          "medical-history",
          "condition",
          conditionId,
          "void",
        ],
        CONDITION_CONFIRM_MULTIPLE: (patientId: string) => [
          "patient",
          "medical-records",
          patientId,
          "medical-history",
          "condition",
          "confirm-multiple",
        ],
        CONDITION_NOTE: (patientId: string, conditionId: string) => [
          "patient",
          "medical-records",
          patientId,
          "medical-history",
          "condition",
          conditionId,
          "note",
        ],
      },
      Medications: {
        AVAILABLE_CHARTING_ACTIONS: (patientId: string) => [
          "patient",
          "medical-records",
          patientId,
          "medications",
          "available-actions",
        ],
        DEFAULT: (patientId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "medications",
        ],
        ADHERENCE_SECTION: (section: string, patientId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "medications",
          section,
        ],
        UPDATE: (patientId: string, medicationStatementId: string) => [
          "patient",
          "medical-records",
          patientId,
          "medications",
          medicationStatementId,
          "update",
        ],
        CONFIRM: (patientId: string, medicationStatementId: string) => [
          "patient",
          "medical-records",
          patientId,
          "medications",
          medicationStatementId,
          "confirm",
        ],
        DENY: (patientId: string, medicationStatementId: string) => [
          "patient",
          "medical-records",
          patientId,
          "medications",
          medicationStatementId,
          "deny",
        ],
        BATCH_CONFIRM: (patientId: string) => [
          "patient",
          "medical-records",
          patientId,
          "medications",
          "batch-confirm",
        ],
        ACTIVATE: (patientId: string, medicationStatementId: string) => [
          "patient",
          "medical-records",
          patientId,
          "medications",
          medicationStatementId,
          "activate",
        ],
        INTENDED: (patientId: string, medicationStatementId: string) => [
          "patient",
          "medical-records",
          patientId,
          "medications",
          medicationStatementId,
          "intended",
        ],
        NOT_TAKEN: (patientId: string, medicationStatementId: string) => [
          "patient",
          "medical-records",
          patientId,
          "medications",
          medicationStatementId,
          "not-taken",
        ],
        STOPPED: (patientId: string, medicationStatementId: string) => [
          "patient",
          "medical-records",
          patientId,
          "medications",
          medicationStatementId,
          "stopped",
        ],
        COMPLETE: (patientId: string, medicationStatementId: string) => [
          "patient",
          "medical-records",
          patientId,
          "medications",
          medicationStatementId,
          "complete",
        ],
        VOID: (patientId: string, medicationStatementId: string) => [
          "patient",
          "medical-records",
          patientId,
          "medications",
          medicationStatementId,
          "void",
        ],
        CREATE: (patientId: string) => [
          "patient",
          "medical-records",
          patientId,
          "medications",
          "create",
        ],
        BATCH_CREATE: (patientId: string) => [
          "patient",
          "medical-records",
          patientId,
          "medications",
          "batch-create",
        ],
        DRUG_NAMES: ["medications", "drugNames"],
        SEARCH_DRUGS: ({
          searchTerm,
          includeDrugs,
          includeMedications,
        }: {
          searchTerm: string;
          includeDrugs: boolean;
          includeMedications: boolean;
        }) => [
          "medications",
          "drugNames",
          searchTerm,
          includeDrugs,
          includeMedications,
        ],
        SEARCH_PERSONALIZED_DRUGS: ({
          patientId,
          searchTerm,
          includeDrugs,
          includeMedications,
        }: {
          patientId: string;
          searchTerm: string;
          includeDrugs: boolean;
          includeMedications: boolean;
        }) => [
          "medications",
          "drugNames",
          "personalizedDrugNames",
          searchTerm,
          patientId,
          includeDrugs,
          includeMedications,
        ],
      },
      RxHistory: {
        GRANT_CONSENT: (patientId: string) => [
          "patient",
          "medical-records",
          patientId,
          "rx-history",
          "grant-consent",
        ],
        DEFAULT: (patientId: string) => [
          "patient",
          "medical-records",
          patientId,
          "rx-history",
        ],
      },
      Profile: {
        DEFAULT: (patientId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "profile",
        ],
        AVAILABLE_ACTIONS: (patientId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "profile",
          "available-actions",
        ],
        DEFAULT_EMERGENCY_CONTACT: (patientId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "profile",
          "emergency-contact",
        ],
        UPDATE_EMERGENCY_CONTACT: (patientId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "profile",
          "emergency-contact",
          "update",
        ],
        PHONE_NUMBER: (patientId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "profile",
          "phone-number",
        ],
        ADDRESS: (patientId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "profile",
          "address",
        ],
        PREFERRED_NAME: (patientId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "profile",
          "preferred-name",
        ],
        GENDER_IDENTITY: (patientId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "profile",
          "gender-identity",
        ],
        PRONOUNS: (patientId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "profile",
          "pronouns",
        ],
      },
      Questionnaires: {
        DEFAULT: (
          patientId?: string,
          questionnairesCategory?: "lifestyle" | "mental-health",
        ) => [
          "patient",
          "medical-records",
          patientId,
          "questionnaires",
          questionnairesCategory,
        ],
        SEND: (patientId: string) => [
          "patient",
          "medical-records",
          patientId,
          "questionnaires",
          "send",
        ],
        CONFIRM: (patientId: string, questionnaireResponseId: string) => [
          "patient",
          "medical-records",
          patientId,
          "questionnaires",
          questionnaireResponseId,
          "confirm",
        ],
        VOID: (patientId: string) => [
          "patient",
          "medical-records",
          patientId,
          "questionnaires",
          "void",
        ],
        AVAILABLE_ACTIONS: (patientId?: string, questionnaireType?: string) => [
          "patient",
          "medical-records",
          patientId,
          "questionnaires",
          "available-actions",
          questionnaireType,
        ],
        HISTORY: (patientId: string, questionnaireType: string) => [
          "patient",
          "medical-records",
          patientId,
          "questionnaires",
          questionnaireType,
          "history",
        ],
        BY_ID: (patientId: string, questionnaireResponseId: string) => [
          "patient",
          "medical-records",
          patientId,
          "questionnaires",
          questionnaireResponseId,
        ],
      },
      MentalHealthQuestionnaire: {
        DEFAULT: (patientId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "mental-health-questionnaire",
        ],
      },
      MetriportHIE: {
        PULL_STATUS: (patientId: string) => [
          "patient",
          "medical-records",
          patientId,
          "metriport-hie",
          "pull-status",
        ],
      },
      MiscellaneousNotes: {
        DEFAULT: (patientId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "miscellaneous-notes",
        ],
        CREATE: (patientId: string) => [
          "patient",
          "medical-records",
          patientId,
          "miscellaneous-notes",
          "create",
        ],
        UPDATE: (patientId: string, procedureId: string) => [
          "patient",
          "medical-records",
          patientId,
          "miscellaneous-notes",
          procedureId,
          "update",
        ],
        RESOLVE: (patientId: string, procedureId: string) => [
          "patient",
          "medical-records",
          patientId,
          "miscellaneous-notes",
          procedureId,
          "resolve",
        ],
        VOID: (patientId: string, procedureId: string) => [
          "patient",
          "medical-records",
          patientId,
          "miscellaneous-notes",
          procedureId,
          "void",
        ],
      },
      SocialHistory: {
        MARITAL_STATUS: (patientId: string) => [
          "patient",
          "medical-records",
          patientId,
          "social-history",
          "marital-status",
        ],
      },
      SurgicalHistory: {
        DEFAULT: (patientId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "surgical-history",
        ],
        CREATE: (patientId: string) => [
          "patient",
          "medical-records",
          patientId,
          "surgical-history",
          "create",
        ],
        VOID: (patientId: string, surgicalHistoryId: string) => [
          "patient",
          "medical-records",
          patientId,
          "surgical-history",
          surgicalHistoryId,
          "void",
        ],
        UPDATE: (patientId: string, surgicalHistoryId: string) => [
          "patient",
          "medical-records",
          patientId,
          "surgical-history",
          surgicalHistoryId,
          "update",
        ],
      },
      Timeline: {
        DEFAULT: (patientId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "timeline",
        ],
        ENCOUNTERS: ({
          patientId,
          tzOffset,
          notAfter,
          eocs,
        }: {
          patientId?: string;
          tzOffset?: number;
          notAfter?: string;
          eocs?: string;
        }) =>
          [
            "patient",
            "medical-records",
            patientId,
            "timeline",
            "encounters",
            tzOffset,
            notAfter,
            eocs,
          ].filter(Boolean),
        ENCOUNTER: ({
          patientId,
          motherEncounterId,
          encounterId,
        }: {
          patientId?: string;
          motherEncounterId?: string;
          encounterId?: string;
        }) =>
          [
            "patient",
            "medical-records",
            patientId,
            "timeline",
            motherEncounterId, // ! motherEncounterId ties the encounter to the related SOAP notes that are attached to the mother encounter
            encounterId,
          ].filter(Boolean),
      },
      Vitals: {
        DEFAULT: (patientId?: string, vitalsType?: string) => [
          "patient",
          "medical-records",
          patientId,
          "vitals",
          vitalsType,
        ],
        CREATE: (patientId?: string, vitalsType?: string) => [
          "patient",
          "medical-records",
          patientId,
          "vitals",
          vitalsType,
        ],
        CORE: (patientId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "vitals",
          "core",
        ],
        BMI: (patientId?: string) => [
          "patient",
          "medical-records",
          patientId,
          "vitals",
          "bmi",
        ],
        HISTORY: (patientId?: string, vitalsType?: string) => [
          "patient",
          "medical-records",
          patientId,
          "vitals",
          vitalsType,
          "history",
        ],
        VOID: (patientId?: string, vitalsType?: string) => [
          "patient",
          "medical-records",
          patientId,
          "vitals",
          vitalsType,
          "void",
        ],
        BMI_HISTORY: (patientId: string) => [
          "patient",
          "medical-records",
          patientId,
          "vitals",
          "bmi",
          "history",
        ],
      },
      ProviderFeedback: {
        CREATE: (patientId: string) => [
          "patient",
          "medical-records",
          patientId,
          "provider-feedback",
          "create",
        ],
      },
    },
    Chart: {
      ALL: ({ patientId }: { patientId?: string }) => [
        "patient",
        "chart",
        patientId,
      ],
      ACTIVE_SESSION: ({
        patientId,
        encounterId,
      }: {
        patientId?: string;
        encounterId?: string;
      }) => ["patient", "chart", patientId, "active-session", encounterId],
      VISIT_CANCEL_REASON_CODES: ["chart", "visit-reason-codes"],
      CANCEL_REASON_CODES: ["chart", "cancel-reason-codes"],
      CLOSE: ["chart", "close"],
      PAUSE: ["chart", "pause"],
      CANCEL: ["chart", "cancel"],
      REJECT_ASYNC_REQUEST: ["chart", "reject-rx-request"],
      CLOSABLE: (patientId: string, encounterId: string) => [
        "patient",
        patientId,
        "chat",
        "encounter",
        encounterId,
        "closable",
      ],
      AVAILABLE_ACTIONS: ({
        patientId,
        motherEncounterId,
      }: {
        patientId: string;
        motherEncounterId: string;
      }) => [
        "patient",
        "chart",
        patientId,
        "encounter",
        motherEncounterId,
        "available-actions",
      ],
      VisitClosure: {
        ADD_BILLING_CODE: ({
          patientId,
          encounterId,
        }: {
          patientId: string;
          encounterId: string;
        }) => [
          "patient",
          "chart",
          patientId,
          "visit-closure",
          "billing-code",
          "add",
          encounterId,
        ],
        ADD_BILLING_CODES: ({
          patientId,
          encounterId,
        }: {
          patientId: string;
          encounterId: string;
        }) => [
          "patient",
          "chart",
          patientId,
          "visit-closure",
          "billing-codes",
          "add",
          encounterId,
        ],
        REMOVE_BILLING_CODES: ({
          patientId,
          encounterId,
        }: {
          patientId: string;
          encounterId: string;
        }) => [
          "patient",
          "chart",
          patientId,
          "visit-closure",
          "billing-codes",
          "remove",
          encounterId,
        ],
        REORDER_BILLING_CODE: ({
          patientId,
          encounterId,
        }: {
          patientId: string;
          encounterId: string;
        }) => [
          "patient",
          "chart",
          patientId,
          "visit-closure",
          "billing-code",
          "reorder",
          encounterId,
        ],
        RELEVANT_DIAGNOSIS_HISTORY: ({
          patientId,
          encounterId,
        }: {
          patientId: string;
          encounterId: string;
        }) => [
          "patient",
          "chart",
          patientId,
          "visit-closure",
          "relevant-diagnosis-history",
          encounterId,
        ],
        BATCH_ADD_DX_FROM_VISIT_DX: (patientId: string) => [
          "patient",
          "chart",
          patientId,
          "visit-closure",
          "batch-add-dx-from-visit-dx",
        ],
      },
      Billing: {
        ENCOUNTER_CODING: ({
          patientId,
          encounterId,
        }: { patientId: string; encounterId: string }) => [
          "patient",
          "chart",
          patientId,
          "billing",
          "encounter-coding",
          encounterId,
        ],
        ICD_10_CODES: ({
          patientId,
          searchTerm,
          concept,
        }: {
          patientId: string;
          searchTerm?: string;
          concept?: string;
        }) => [
          "patient",
          "chart",
          patientId,
          "billing",
          "icd10-codes",
          searchTerm,
          concept,
        ],
        START_ENCOUNTER_TIME: ({
          patientId,
          encounterId,
        }: { patientId: string; encounterId: string }) => [
          "patient",
          "chart",
          patientId,
          "billing",
          "encounter-time",
          encounterId,
          "start",
        ],
        COMPLETE_ENCOUNTER_TIME: ({
          patientId,
          encounterId,
        }: { patientId: string; encounterId: string }) => [
          "patient",
          "chart",
          patientId,
          "billing",
          "encounter-time",
          encounterId,
          "complete",
        ],
        CPT_CODES: ({
          patientId,
          patientType,
          billType,
          encounterTimeMinutes,
          visitType,
        }: {
          patientId: string;
          patientType: "new" | "established" | null;
          billType: "duration" | "mdm";
          encounterTimeMinutes: number;
          visitType: "av" | "chat" | null;
        }) => [
          "patient",
          "chart",
          patientId,
          "billing",
          "cpt-codes",
          patientType,
          billType,
          encounterTimeMinutes,
          visitType,
        ],
        CLAIM_SUBMIT: ({
          patientId,
          encounterId,
        }: { patientId: string; encounterId: string }) => [
          "patient",
          "chart",
          patientId,
          "billing",
          "claim-submit",
          encounterId,
        ],
      },
      Eligibility: {
        DEFAULT: (patientId: string) => [
          "patient",
          "chart",
          patientId,
          "eligibility",
        ],
      },
      FormularyLookup: {
        DEFAULT: (
          patientId: string,
          patientEligibilityId: number,
          ndc: string,
        ) => [
          "patient",
          "chart",
          patientId,
          "formulary-lookup",
          patientEligibilityId,
          ndc,
        ],
      },
      PrescriptionBenefits: {
        DEFAULT: (
          patientId: string,
          ndc: string,
          pharmacyId: number,
          quantity: number,
          daysSupply: number,
          dispenseUnitTypeId: number,
          patientEligibilityId?: number,
        ) => [
          "patient",
          "chart",
          patientId,
          "prescription-benefits",
          ndc,
          pharmacyId,
          quantity,
          daysSupply,
          dispenseUnitTypeId,
          patientEligibilityId,
        ],
      },
      Prescriptions: {
        INTERACTION_CHECKING: ({
          patientId,
          medicationRequestId,
          dispensableDrugId,
        }: {
          patientId: string;
          medicationRequestId: string;
          dispensableDrugId: string;
        }) => [
          "patient",
          "chart",
          patientId,
          "prescriptions",
          "interaction-checking",
          medicationRequestId,
          dispensableDrugId,
        ],
        ORDER_ENTRY_ACTIONS: ({ patientId }: { patientId: string }) => [
          "patient",
          "chart",
          patientId,
          "prescriptions",
          "order-entry-available-actions",
        ],
        DEFAULT_PATIENT: (patientId?: string) => [
          "patient",
          "chart",
          patientId,
          "prescriptions",
        ],
        DEFAULT: (patientId: string, encounterId: string) => [
          "patient",
          "chart",
          patientId,
          "prescriptions",
          encounterId,
        ],
        CREATE: (patientId: string, encounterId: string) => [
          "patient",
          "chart",
          patientId,
          "prescriptions",
          encounterId,
          "create",
        ],
        RENEW_PRESCRIPTIONS: ({
          patientId,
          encounterId,
        }: { patientId: string; encounterId: string }) => [
          "patient",
          "chart",
          patientId,
          "prescriptions",
          encounterId,
          "renew-prescriptions",
        ],
        UPDATE: (
          patientId: string,
          encounterId: string,
          medicationRequestId: string,
        ) => [
          "patient",
          "chart",
          patientId,
          "prescriptions",
          encounterId,
          medicationRequestId,
          "update",
        ],
        CANCEL: (
          patientId: string,
          encounterId: string,
          medicationRequestId: string,
        ) => [
          "patient",
          "chart",
          patientId,
          "prescriptions",
          encounterId,
          medicationRequestId,
          "cancel",
        ],
        MARK_ROUTED: (
          patientId: string,
          encounterId: string,
          medicationRequestId: string,
        ) => [
          "patient",
          "chart",
          patientId,
          "prescriptions",
          encounterId,
          medicationRequestId,
          "mark-routed",
        ],
        ROUTE_PHARMACY: (
          patientId: string,
          encounterId: string,
          medicationRequestId: string,
        ) => [
          "patient",
          "chart",
          patientId,
          "prescriptions",
          encounterId,
          medicationRequestId,
          "route-pharmacy",
        ],
        ROUTE_PHARMACY_ALL: (patientId: string) => [
          "patient",
          "chart",
          patientId,
          "prescriptions",
          "route-pharmacy-all",
        ],
        DEFAULT_OTC: (patientId: string, encounterId: string) => [
          "patient",
          "chart",
          patientId,
          "prescriptions",
          encounterId,
          "otc",
        ],
        CREATE_OTC: (patientId: string, encounterId: string) => [
          "patient",
          "chart",
          patientId,
          "prescriptions",
          encounterId,
          "create-otc",
        ],
        UPDATE_OTC: (
          patientId: string,
          encounterId: string,
          carePlanActivityId: string,
        ) => [
          "patient",
          "chart",
          patientId,
          "prescriptions",
          encounterId,
          "update-otc",
          carePlanActivityId,
        ],
        DELETE_OTC: (
          patientId: string,
          encounterId: string,
          carePlanActivityId: string,
        ) => [
          "patient",
          "chart",
          patientId,
          "prescriptions",
          encounterId,
          "delete-otc",
          carePlanActivityId,
        ],
        BATCH_APPLY_PHARMACY: (patientId: string, encounterId: string) => [
          "patient",
          "chart",
          patientId,
          "prescriptions",
          encounterId,
          "batch-apply-pharmacy",
        ],
        RENEWABLE_PRESCRIPTIONS: ({
          patientId,
          encounterId,
        }: { patientId: string; encounterId: string }) => [
          "patient",
          "chart",
          patientId,
          "prescriptions",
          encounterId,
          "renewable-prescriptions",
        ],
        ALTERNATIVE_DISPENSING_PHARMACIES: ({
          patientId,
          encounterId,
          medicationRequestId,
        }: {
          patientId: string;
          encounterId: string;
          medicationRequestId: string;
        }) => [
          "patient",
          "chart",
          patientId,
          "prescriptions",
          encounterId,
          "alternative-dispensing-pharmacies",
          medicationRequestId,
        ],
      },
      RxRecommendations: {
        ROOT: (patientId: string) => [
          "patient",
          "chart",
          patientId,
          "rx-recommendations",
        ],
        EOC: ({
          patientId,
          eocId,
        }: {
          patientId: string;
          eocId: string;
        }) => [
          ...QueryKeys.Patient.Chart.RxRecommendations.ROOT(patientId),
          "eoc",
          eocId,
        ],
        SNOMED_CODES: ({
          patientId,
          partnerUid,
          snomedCodes,
        }: {
          patientId: string;
          partnerUid: string;
          snomedCodes: string[];
        }) => [
          ...QueryKeys.Patient.Chart.RxRecommendations.ROOT(patientId),
          "snomed-codes",
          partnerUid,
          Array.from(new Set(snomedCodes)).sort(),
        ],
      },
      Labs: {
        SEARCH_COMPENDIUM: ({
          patientId,
          labNetwork,
          compendiumId,
          searchTerm,
          requestGroupId,
        }: {
          patientId: string;
          labNetwork: (typeof RequestGroup.LAB_NETWORKS)[keyof typeof RequestGroup.LAB_NETWORKS];
          compendiumId: string;
          searchTerm: string;
          requestGroupId?: string;
        }) => [
          "patient",
          "chart",
          patientId,
          "labs",
          "search-compendium",
          labNetwork,
          compendiumId,
          searchTerm,
          requestGroupId,
        ],
        SEARCH: ({
          patientId,
          network,
          billingMethod,
          query,
          requestGroupId,
        }: {
          patientId: string;
          network: (typeof ServiceRequest.LAB_NETWORKS)[keyof typeof ServiceRequest.LAB_NETWORKS];
          billingMethod: (typeof RequestGroup.BILLING_METHODS)[keyof typeof RequestGroup.BILLING_METHODS];
          query: string;
          requestGroupId?: string;
        }) => [
          "patient",
          "chart",
          patientId,
          "labs",
          "search",
          network,
          billingMethod,
          query,
          requestGroupId,
        ],
        ADD_TEST_TO_ORDER: ({
          patientId,
          requestGroupId,
        }: {
          patientId: string;
          requestGroupId: string;
        }) => [
          "patient",
          "chart",
          patientId,
          "labs",
          "order",
          requestGroupId,
          "add-test",
        ],
        ORDER_ENTRY_ACTIONS: ({ patientId }: { patientId: string }) => [
          "patient",
          "chart",
          patientId,
          "labs",
          "order-entry-available-actions",
        ],
        DEFAULT: ({
          patientId,
          encounterId,
        }: { patientId: string; encounterId: string }) => [
          "patient",
          "chart",
          patientId,
          "labs",
          encounterId,
        ],
        DEFAULT_V2: ({
          patientId,
          encounterId,
        }: { patientId: string; encounterId: string }) => [
          "patient",
          "chart",
          patientId,
          "labs",
          encounterId,
          "v2",
        ],
        CREATE_ORDER: ({
          patientId,
          encounterId,
        }: { patientId: string; encounterId: string }) => [
          "patient",
          "chart",
          patientId,
          "labs",
          encounterId,
          "create-order",
        ],
        UPDATE_ORDER: ({
          patientId,
          encounterId,
          requestGroupId,
        }: {
          patientId: string;
          encounterId: string;
          requestGroupId: string;
        }) => [
          "patient",
          "chart",
          patientId,
          "labs",
          encounterId,
          requestGroupId,
          "update-order",
        ],
        CHANGE_PSC: ({
          patientId,
          encounterId,
          requestGroupId,
        }: {
          patientId: string;
          encounterId: string;
          requestGroupId: string;
        }) => [
          "patient",
          "chart",
          patientId,
          "labs",
          encounterId,
          requestGroupId,
          "change-psc",
        ],
        DELETE_ORDER: ({
          patientId,
          encounterId,
          requestGroupId,
        }: {
          patientId: string;
          encounterId: string;
          requestGroupId: string;
        }) => [
          "patient",
          "chart",
          patientId,
          "labs",
          encounterId,
          requestGroupId,
          "delete-order",
        ],
        CANCEL_ORDER: ({
          patientId,
          encounterId,
          requestGroupId,
        }: {
          patientId: string;
          encounterId: string;
          requestGroupId: string;
        }) => [
          "patient",
          "chart",
          patientId,
          "labs",
          encounterId,
          requestGroupId,
          "cancel-order",
        ],
        AOE_QUESTIONNAIRE: ({
          patientId,
          encounterId,
          requestGroupId,
        }: {
          patientId: string;
          encounterId: string;
          requestGroupId: string;
        }) => [
          "patient",
          "chart",
          patientId,
          "labs",
          encounterId,
          requestGroupId,
          "aoe-questionnaire",
        ],
        UPDATE_AOE_QUESTIONNAIRE: ({
          patientId,
          encounterId,
          requestGroupId,
        }: {
          patientId: string;
          encounterId: string;
          requestGroupId: string;
        }) => [
          "patient",
          "chart",
          patientId,
          "labs",
          encounterId,
          requestGroupId,
          "aoe-questionnaire",
          "update",
        ],
        AOE_FOR_ORDER: ({
          patientId,
          requestGroupId,
        }: {
          patientId: string;
          requestGroupId: string;
        }) => [
          "patient",
          "chart",
          patientId,
          "labs",
          requestGroupId,
          "aoe-for-order",
        ],
        REQUEST_PAYMENT: ({
          patientId,
          encounterId,
          requestGroupId,
        }: {
          patientId: string;
          encounterId: string;
          requestGroupId: string;
        }) => [
          "patient",
          "chart",
          patientId,
          "labs",
          encounterId,
          requestGroupId,
          "request-payment",
        ],
        SUBMIT_ORDER: ({
          patientId,
          encounterId,
          requestGroupId,
        }: {
          patientId: string;
          encounterId: string;
          requestGroupId: string;
        }) => [
          "patient",
          "chart",
          patientId,
          "labs",
          encounterId,
          requestGroupId,
          "submit",
        ],
        CREATE_TEST: ({
          patientId,
          encounterId,
          requestGroupId,
          clientPay,
        }: {
          patientId: string;
          encounterId: string;
          requestGroupId: string;
          clientPay: boolean;
        }) => [
          "patient",
          "chart",
          patientId,
          "labs",
          encounterId,
          requestGroupId,
          clientPay,
          "create-test",
        ],
        UPDATE_TEST: ({
          patientId,
          encounterId,
          requestGroupId,
          serviceRequestId,
        }: {
          patientId: string;
          encounterId: string;
          requestGroupId: string;
          serviceRequestId: string;
        }) => [
          "patient",
          "chart",
          patientId,
          "labs",
          encounterId,
          requestGroupId,
          serviceRequestId,
          "update-test",
        ],
        DELETE_TEST: ({
          patientId,
          encounterId,
          requestGroupId,
          serviceRequestId,
        }: {
          patientId: string;
          encounterId: string;
          requestGroupId: string;
          serviceRequestId: string;
        }) => [
          "patient",
          "chart",
          patientId,
          "labs",
          encounterId,
          requestGroupId,
          serviceRequestId,
          "remove-test",
        ],
        SAVE_AOE: ({
          patientId,
          encounterId,
          requestGroupId,
        }: {
          patientId: string;
          encounterId: string;
          requestGroupId: string;
        }) => [
          "patient",
          "chart",
          patientId,
          "labs",
          encounterId,
          requestGroupId,
          "aoe-save",
        ],
      },
      EpisodesOfCare: {
        DEFAULT: ({
          patientId,
          encounterId,
        }: { patientId?: string; encounterId?: string }) =>
          [
            "patient",
            "chart",
            patientId,
            "episode-of-care",
            encounterId,
          ].filter(Boolean),
        CREATE: ({ patientId }: { patientId: string }) => [
          "patient",
          "chart",
          patientId,
          "episode-of-care",
          "create",
        ],
        CarePlan: {
          CREATE: (patientId: string) => [
            "patient",
            "chart",
            patientId,
            "episode-of-care",
            "careplan",
            "create",
          ],
          DELETE: (patientId: string) => [
            "patient",
            "chart",
            patientId,
            "episode-of-care",
            "careplan",
            "delete",
          ],
          PREVIEW: ({
            patientId,
            carePlanId,
          }: { patientId: string; carePlanId: string }) => [
            "patient",
            "chart",
            patientId,
            "episode-of-care",
            "careplan",
            carePlanId,
            "preview",
          ],
          TRANSLATE: ({
            patientId,
            encounterId,
            carePlanId,
          }: {
            patientId: string;
            encounterId: string;
            carePlanId: string;
          }) => [
            "patient",
            "chart",
            patientId,
            "episode-of-care",
            encounterId,
            "careplan",
            carePlanId,
            "translate",
          ],
          SUMMARY: ({
            patientId,
            encounterId,
          }: { patientId: string; encounterId: string }) => [
            "patient",
            "chart",
            patientId,
            "episode-of-care",
            encounterId,
            "careplan",
            "summary",
          ],
          SEND: ({
            carePlanId,
            patientId,
          }: { carePlanId: string; patientId: string }) => [
            "patient",
            "chart",
            patientId,
            "episode-of-care",
            "careplan",
            carePlanId,
            "send",
          ],
          AVAILABLE_ACTIONS: ({
            patientId,
            encounterId,
          }: { patientId: string; encounterId: string }) => [
            "patient",
            "chart",
            patientId,
            "episode-of-care",
            encounterId,
            "careplan",
            "available-actions",
          ],
          Referrals: {
            UPDATE: ["charting", "episode-of-care", "referral-update"],
            TOGGLE: ["charting", "episode-of-care", "referral-toggle"],
          },
          Recommendation: {
            DEFAULT: ({
              patientId,
              encounterId,
              carePlanId,
            }: {
              patientId: string;
              encounterId: string;
              carePlanId: string;
            }) => [
              "patient",
              "chart",
              patientId,
              "episode-of-care",
              encounterId,
              "careplan",
              carePlanId,
              "recommendation",
            ],
            CREATE: ({
              patientId,
              encounterId,
              carePlanId,
            }: {
              patientId: string;
              encounterId: string;
              carePlanId: string;
            }) => [
              "patient",
              "chart",
              patientId,
              "episode-of-care",
              encounterId,
              "careplan",
              carePlanId,
              "recommendation-create",
            ],
            CREATE_MULTIPLE: ({
              patientId,
              encounterId,
              carePlanId,
            }: {
              patientId: string;
              encounterId: string;
              carePlanId: string;
            }) => [
              "patient",
              "chart",
              patientId,
              "episode-of-care",
              encounterId,
              "careplan",
              carePlanId,
              "recommendations-create",
            ],
            UPDATE: ({
              patientId,
              encounterId,
              carePlanId,
            }: {
              patientId: string;
              encounterId: string;
              carePlanId: string;
            }) => [
              "patient",
              "chart",
              patientId,
              "episode-of-care",
              encounterId,
              "careplan",
              carePlanId,
              "recommendation-update",
            ],
            DELETE: ({
              patientId,
              encounterId,
              carePlanId,
            }: {
              patientId: string;
              encounterId: string;
              carePlanId: string;
            }) => [
              "patient",
              "chart",
              patientId,
              "episode-of-care",
              encounterId,
              "careplan",
              carePlanId,
              "recommendation-delete",
            ],
            DELETE_ALL: ({
              patientId,
              encounterId,
              carePlanId,
            }: {
              patientId: string;
              encounterId: string;
              carePlanId: string;
            }) => [
              "patient",
              "chart",
              patientId,
              "episode-of-care",
              encounterId,
              "careplan",
              carePlanId,
              "recommendations-delete-all",
            ],
            // Shared mutation key for BOTH single and bulk recommendation void
            // mutations, scoped to a careplan. Lets the UI use `useIsMutating`
            // to prevent a single + bulk void from being in flight at once,
            // which would otherwise cause a FHIR 409 lost-update conflict
            // (EHR-8188).
            VOID: ({
              patientId,
              encounterId,
              carePlanId,
            }: {
              patientId: string;
              encounterId: string;
              carePlanId: string;
            }) => [
              "patient",
              "chart",
              patientId,
              "episode-of-care",
              encounterId,
              "careplan",
              carePlanId,
              "recommendation-void",
            ],
          },
          Disposition: {
            CODES: ["charting", "episode-of-care", "disposition-codes"],
            DEFAULT: ({
              patientId,
              encounterId,
              carePlanId,
            }: {
              patientId: string;
              encounterId: string;
              carePlanId: string;
            }) => [
              "patient",
              "chart",
              patientId,
              "episode-of-care",
              encounterId,
              "careplan",
              carePlanId,
              "disposition",
            ],
            CREATE: ({
              patientId,
              encounterId,
              carePlanId,
            }: {
              patientId: string;
              encounterId: string;
              carePlanId: string;
            }) => [
              "patient",
              "chart",
              patientId,
              "episode-of-care",
              encounterId,
              "careplan",
              carePlanId,
              "disposition-create",
            ],
            UPDATE: ({
              patientId,
              encounterId,
              carePlanId,
            }: {
              patientId: string;
              encounterId: string;
              carePlanId: string;
            }) => [
              "patient",
              "chart",
              patientId,
              "episode-of-care",
              encounterId,
              "careplan",
              carePlanId,
              "disposition-update",
            ],
            DELETE: ({
              patientId,
              encounterId,
              carePlanId,
            }: {
              patientId: string;
              encounterId: string;
              carePlanId: string;
            }) => [
              "patient",
              "chart",
              patientId,
              "episode-of-care",
              encounterId,
              "careplan",
              carePlanId,
              "disposition-delete",
            ],
          },
          FollowUp: {
            DEFAULT: ({
              patientId,
              childEncounterId,
              carePlanId,
            }: {
              patientId: string;
              childEncounterId: string;
              carePlanId: string;
            }) => [
              "patient",
              "chart",
              patientId,
              "episode-of-care",
              childEncounterId,
              "careplan",
              carePlanId,
              "followup",
            ],
            CREATE: ({
              patientId,
              childEncounterId,
              carePlanId,
            }: {
              patientId: string;
              childEncounterId: string;
              carePlanId: string;
            }) => [
              "patient",
              "chart",
              patientId,
              "episode-of-care",
              childEncounterId,
              "careplan",
              carePlanId,
              "followup-create",
            ],
            UPDATE: ({
              patientId,
              childEncounterId,
              carePlanId,
            }: {
              patientId: string;
              childEncounterId: string;
              carePlanId: string;
            }) => [
              "patient",
              "chart",
              patientId,
              "episode-of-care",
              childEncounterId,
              "careplan",
              carePlanId,
              "followup-update",
            ],
            DELETE: ({
              patientId,
              childEncounterId,
              carePlanId,
            }: {
              patientId: string;
              childEncounterId: string;
              carePlanId: string;
            }) => [
              "patient",
              "chart",
              patientId,
              "episode-of-care",
              childEncounterId,
              "careplan",
              carePlanId,
              "followup-delete",
            ],
          },
        },
        Assessment: {
          DEFAULT: ({
            patientId,
            encounterId,
          }: { patientId?: string; encounterId?: string }) => [
            "patient",
            "chart",
            patientId,
            "episode-of-care",
            encounterId,
            "assessment",
          ],
          CREATE: (patientId?: string) => [
            "patient",
            "chart",
            patientId,
            "episode-of-care",
            "assessment",
            "create",
          ],
          UPDATE: (patientId?: string) => [
            "patient",
            "chart",
            patientId,
            "episode-of-care",
            "assessment",
            "update",
          ],
          DELETE: (patientId?: string, docRefId?: string) => [
            "patient",
            "chart",
            patientId,
            "episode-of-care",
            "assessment",
            "delete",
            docRefId,
          ],
          GENERATE: (patientId?: string, encounterId?: string) => [
            "patient",
            "chart",
            patientId,
            "episode-of-care",
            encounterId,
            "assessment",
            "generate",
          ],
          GENERATION_STATUS: (patientId?: string, encounterId?: string) => [
            "patient",
            "chart",
            patientId,
            "episode-of-care",
            encounterId,
            "assessment",
            "generation-status",
          ],
        },
      },
      Conditions: {
        DEFAULT: (patientId: string) => [
          "patient",
          "chart",
          patientId,
          "conditions",
        ],
        VisitDiagnoses: ({
          patientId,
          encounterId,
        }: {
          patientId: string;
          encounterId?: string;
        }) => [
          "patient",
          "chart",
          patientId,
          "conditions",
          "visit-diagnoses",
          encounterId,
        ],
        Search: {
          DEFAULT: ({
            patientId,
            searchTerm,
            motherEncounterId,
          }: {
            patientId: string;
            searchTerm: string;
            motherEncounterId: string;
          }) =>
            [
              "patient",
              "chart",
              patientId,
              "conditions",
              motherEncounterId,
              searchTerm,
            ].filter(Boolean),
        },
      },
      Subjective: {
        DEFAULT: ({
          patientId,
          encounterId,
        }: { patientId?: string; encounterId?: string }) => [
          "patient",
          "chart",
          patientId,
          "subjective",
          encounterId,
        ],
        CREATE: (patientId?: string, encounterId?: string) => [
          "patient",
          "chart",
          patientId,
          "subjective",
          "create",
          encounterId,
        ],
        UPDATE: (patientId?: string, docRefId?: string) => [
          "patient",
          "chart",
          patientId,
          "subjective",
          "update",
          docRefId,
        ],
      },
      AVCallConditions: {
        DEFAULT: ({
          patientId,
          encounterId,
        }: { patientId?: string; encounterId?: string }) => [
          "patient",
          "chart",
          patientId,
          "av-call-conditions",
          encounterId,
        ],
        MARK_CONDITION_ADDED_TO_CHART: (patientId: string) => [
          "patient",
          "chart",
          patientId,
          "av-call-conditions",
          "condition-added-to-chart",
        ],
        MARK_EXTRACTION_AS_ERRONEOUS: (patientId: string) => [
          "patient",
          "chart",
          patientId,
          "av-call-conditions",
          "marked-as-erroneous",
        ],
      },
      Observation: {
        DEFAULT: ({
          patientId,
          encounterId,
        }: { patientId?: string; encounterId?: string }) => [
          "patient",
          "chart",
          patientId,
          "observation",
          encounterId,
        ],
        CREATE_GENERIC: ({
          patientId,
          encounterId,
        }: { patientId?: string; encounterId?: string }) => [
          "patient",
          "chart",
          patientId,
          "observation",
          encounterId,
          "generic",
          "create",
        ],
        UPDATE_GENERIC: ({
          patientId,
          encounterId,
          observationId,
        }: {
          patientId?: string;
          encounterId?: string;
          observationId?: string;
        }) => [
          "patient",
          "chart",
          patientId,
          "observation",
          encounterId,
          "generic",
          observationId,
          "update",
        ],
        VOID_GENERIC: ({
          patientId,
          encounterId,
          observationId,
        }: {
          patientId?: string;
          encounterId?: string;
          observationId?: string;
        }) => [
          "patient",
          "chart",
          patientId,
          "observation",
          encounterId,
          "generic",
          observationId,
          "void",
        ],
        FILL_NORMAL_EXAM: ({
          patientId,
          encounterId,
        }: { patientId?: string; encounterId?: string }) => [
          "patient",
          "chart",
          patientId,
          "observation",
          encounterId,
          "generic",
          "fill-normal-exam",
        ],
      },
      AmazonVisitIntake: {
        DEFAULT: ({
          patientId,
          amazonVisitId,
        }: { patientId: string; amazonVisitId: string }) => [
          "amazon-visit-intake",
          patientId,
          amazonVisitId,
        ],
        MARK_MEDICATION_ADDED_TO_CHART: [
          "amazon-clinic",
          "medication-extractions",
          "medication-added-to-chart",
        ],
        MARK_CONDITION_ADDED_TO_CHART: [
          "amazon-clinic",
          "condition-extractions",
          "condition-added-to-chart",
        ],
        MARK_ALLERGEN_ADDED_TO_CHART: [
          "amazon-clinic",
          "allergy-extractions",
          "allergen-added-to-chart",
        ],
        MARK_MEDICATION_EXTRACTION_AS_ERRONEOUS: [
          "amazon-clinic",
          "medication-extractions",
          "marked-as-erroneous",
        ],
        MARK_CONDITION_EXTRACTION_AS_ERRONEOUS: [
          "amazon-clinic",
          "condition-extractions",
          "marked-as-erroneous",
        ],
      },
      AmazonVisitHistory: {
        DEFAULT: (amazonVisitId?: string) => [
          "amazon-visit-history",
          amazonVisitId,
        ],
      },
      EncounterFiles: ({
        patientId,
        motherEncounterId,
      }: { patientId: string; motherEncounterId: string }) => [
        "patient",
        "chart",
        patientId,
        "encounter-files",
        motherEncounterId,
      ],
    },
    Chat: {
      AVAILABLE_ACTIONS: (patientId: string, encounterId?: string) => [
        "patient",
        "chat",
        patientId,
        "available-actions",
        encounterId,
      ],
      CareTeam: {
        DEFAULT: (patientId: string) => [
          "patient",
          "chat",
          "care-team",
          patientId,
        ],
        AVAILABLE_PRACITIONERS: (patientId: string) => [
          "patient",
          "chat",
          "care-team",
          "available-practitioners",
          patientId,
        ],
        ADD_MEMBER: (patientId: string) => [
          "patient",
          "chat",
          "care-team",
          "add-member",
          patientId,
        ],
        REMOVE_MEMBER: (patientId: string) => [
          "patient",
          "chat",
          "care-team",
          "remove-member",
          patientId,
        ],
        OUT_OF_OFFICE_COVERING_PROVIDER: (
          patientId?: string,
          practitionerId?: string,
        ) => [
          "patient",
          patientId,
          "chat",
          "care-team",
          "out-of-office",
          "covering-provider",
          practitionerId,
        ],
      },
      Messages: {
        DEFAULT: (roomToken?: string, filterMode?: ChatFilterMode) => [
          "chat",
          "messages",
          roomToken,
          filterMode,
        ],
        UNIVERSAL_CLINIC: (roomId: string) => [
          "patient",
          "chat",
          "messages",
          "universal-clinic",
          roomId,
        ],
      },
      TRANSCRIBE: ["chat", "transcribe"],
      Encounter: {
        DEFAULT: (patientId?: string, encounterId?: string) => [
          "patient",
          "chat",
          patientId,
          "encounter",
          encounterId,
        ],
      },
    },
    RoomToken: {
      DEFAULT: (patientId: string, encounterId?: string) => [
        "patient",
        "room-token",
        patientId,
        encounterId,
      ],
    },
    Files: {
      DEFAULT: ({ patientId }: { patientId: string }) => [
        "patient",
        patientId,
        "files",
      ],
      S3: ({ patientId, path }: { patientId: string; path: string }) => [
        "patient",
        patientId,
        "files",
        path,
      ],
      RESOURCE: ({
        patientId,
        resourceType,
        resourceId,
      }: {
        patientId: string;
        resourceType?: string;
        resourceId?: string;
      }) => ["patient", patientId, "files", resourceType, resourceId],
      AMAZON: ({
        patientId,
        encounterId,
        filename,
      }: {
        patientId: string;
        encounterId: string;
        filename: string;
      }) => ["patient", patientId, "files", "amazon", encounterId, filename],
    },
    Encounter: {
      Assessment: {
        FEEDBACK: (patientId?: string, encounterId?: string) => [
          "patient",
          patientId,
          "encounter",
          encounterId,
          "assessment",
          "feedback",
        ],
      },
    },
    EpisodesOfCare: {
      // `include` is part of the key so the Timeline's option list, which asks for
      // EpisodeOfCare-less visit diagnoses, is never served to the Files pickers from cache.
      // Callers that omit it still prefix-match both variants when invalidating.
      LIST: (patientId: string, include?: string) =>
        ["patient", patientId, "episodes-of-care-list", include].filter(
          Boolean,
        ),
    },
    Unmatched: {
      MARK_UNSOLICITED_DX_REPORT_RESOLVED: (
        patientId: string,
        diagnosticReportId: string,
      ) => [
        "patient",
        patientId,
        "unmatched",
        "dx-report",
        "resolve",
        diagnosticReportId,
      ],
    },
  },
  Distribution: {
    DEFAULT: ["distribution"],
    AVAILABLE_ACTIONS: (params: {
      encounterId?: string;
      patientId: string;
    }) =>
      [
        "distribution",
        "available-actions",
        params.patientId,
        params.encounterId,
      ].filter((key) => key !== undefined),
    TAKE: ["distribution", "take-encounter"],
    HANDOFF: ["distribution", "handoff-encounter"],
    STEAL: ["distribution", "steal-encounter"],
    START_FORCED_LIVE_CHAT: (patientId: string) => [
      "distribution",
      "start-forced-live-chat",
      patientId,
    ],
  },
  Snomed: {
    SEARCH: (searchTerm: string) => ["snomed", "codes", "search", searchTerm],
  },
  FEATURE_FLAGS: {
    DEFAULT: ["feature-flags"],
    CONFIG_CAT_FLAG: (flagName: string) => [
      "feature-flags",
      "config-cat",
      flagName,
    ],
  },
  VIDEO_VISIT: {
    DEFAULT: ["video-visit"],
    COMPLETE: ["video-visit", "complete"],
  },
  HealthChecks: {
    DEFAULT: ["health-checks"],
    ERX: ["health-checks", "erx"],
  },
  Clinic: {
    Notifications: {
      DEFAULT: ["clinic", "notifications"],
    },
  },
  Labs: {
    SEARCH_PSC_LOCATIONS: ({
      labNetwork,
      city,
      state,
      zip,
    }: {
      labNetwork: (typeof ServiceRequest.LAB_NETWORKS)[keyof typeof ServiceRequest.LAB_NETWORKS];
      city?: string;
      state?: string;
      zip?: string;
    }) => ["labs", "search-psc-locations", labNetwork, city, state, zip],
  },
  // Org-level token — no patientId in the request or response.
  // Patient context is added client-side when assembling the embed URL.
  MetriportHIE: {
    EMBED_TOKEN: ["metriport-hie", "embed-token"],
  },
  Reference: {
    Allergens: {
      COMMON: ["reference", "allergens", "common"],
      SEARCH: (searchTerm?: string) => [
        "reference",
        "allergens",
        "search",
        searchTerm,
      ],
    },
    Immunization: {
      VACCINE_CODE_SEARCH: (searchTerm: string) => [
        "reference",
        "immunization",
        "vaccine-code-search",
        searchTerm,
      ],
    },
    Files: {
      CATEGORIES: ["reference", "files", "categories"],
    },
    Codes: {
      ICD10_SEARCH: (searchTerm: string) => [
        "reference",
        "codes",
        "icd10-search",
        searchTerm,
      ],
    },
    DrugUnits: ["reference", "drug-units"],
    Formulary: {
      Search: {
        DEFAULT: ({
          searchTerm,
          partnerUid,
          snomedCode,
        }: {
          searchTerm: string;
          partnerUid?: string;
          snomedCode?: string;
        }) => [
          "reference",
          "formulary",
          "search",
          searchTerm,
          partnerUid,
          snomedCode,
        ],
      },
    },
    Referrals: {
      REASON_CODES: ["reference", "referrals", "reason-codes"],
      URGENCY_CODES: ["reference", "referrals", "urgency-codes"],
      PROVIDER_CODES: ["reference", "referrals", "provider-codes"],
    },
  },
};

export const AVAILABLE_ACTIONS_QUERY_KEYS = [
  QueryKeys.Distribution.AVAILABLE_ACTIONS({
    patientId: "*",
  }),
  QueryKeys.Patient.MedicalRecords.Profile.AVAILABLE_ACTIONS("*"),
  QueryKeys.Patient.MedicalRecords.Allergies.AVAILABLE_CHARTING_ACTIONS("*"),
  QueryKeys.Patient.MedicalRecords.Medications.AVAILABLE_CHARTING_ACTIONS("*"),
  QueryKeys.Patient.Chart.AVAILABLE_ACTIONS({
    patientId: "*",
    motherEncounterId: "*",
  }),
  QueryKeys.Patient.Chart.EpisodesOfCare.CarePlan.AVAILABLE_ACTIONS({
    patientId: "*",
    encounterId: "*",
  }),
  QueryKeys.Patient.Chart.Prescriptions.ORDER_ENTRY_ACTIONS({ patientId: "*" }),
  QueryKeys.Patient.Chart.Labs.ORDER_ENTRY_ACTIONS({ patientId: "*" }),
  QueryKeys.Patient.Chart.EpisodesOfCare.Assessment.DEFAULT({
    patientId: "*",
    encounterId: "*",
  }),
  QueryKeys.Patient.Chat.AVAILABLE_ACTIONS("*", "*"),
  QueryKeys.Patient.MedicalRecords.Labs.ORDER_HISTORY_AVAILABLE_ACTIONS("*"),
  QueryKeys.Patient.MedicalRecords.WorkNotes.AVAILABLE_ACTIONS("*"),
  // Currently CarePlan summary also pulls in available actions, we will probably want to split this out in the
  // future for sanity's sake but for now we will need to invalidate this query key to get careplan actions
  QueryKeys.Patient.Chart.EpisodesOfCare.CarePlan.SUMMARY({
    patientId: "*",
    encounterId: "*",
  }),
];

export const CHAT_QUERY_KEYS = [
  // Cast "*" as ChatFilterMode for wildcard matching in wildcardInvalidateQuery
  QueryKeys.Patient.Chat.Messages.DEFAULT("*", "*" as ChatFilterMode),
  QueryKeys.Patient.Chat.Messages.UNIVERSAL_CLINIC("*"),
];
